/**
 * F1 · অ্যাকাউন্ট হোম — `/account`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame F1 (360 × 680).
 *
 * Frame note: "Two named groups, six rows. Cash sits on white directly under the header
 * so it reads as an account fact, not a promotion — and the label is অপ্রাপ্য ক্যাশ
 * throughout." `ব্যালেন্স` is not used here or anywhere (docs/02 § terminology).
 *
 * KNOWN DEFECT 1 (one instance on this frame): the phone number `০১৭১২ ৩৪৫ ৬৭৮` under
 * the name is drawn `font:400 14px/1.4 Manrope` with `tabular-nums`. Those are Bangla
 * numerals and Manrope has none, so the string falls back silently. Set here in Anek
 * Bangla at the drawn 14px; `tabular-nums` is dropped with it, because it is a Manrope
 * feature and the digits are no longer Manrope's.
 *
 * THE LEADING STAYS AT THE DRAWN 1.4, and that correction is the whole of this screen's
 * shift bug. docs/02's small band (below 22px → 1.75) is a rule about Bangla WORDS,
 * where the conjuncts need the room; the design applies it to Bangla words and sets its
 * numeral lines tighter — 1.4 here, 1.15 on the cash figure, 1.3 on E2's clock. Raising
 * this one line to 1.75 grew its box from 19.6px to 24.5px, which pushed the name+phone
 * column past the 54px avatar and made the teal header 94px where the design draws 90px.
 * Every row below it then sat four pixels low: the whole frame was off by exactly the
 * one value substituted here. Substituting the FAMILY is the defect fix and is kept;
 * substituting the METRICS was not, and is reverted.
 *
 * `/account/notifications` (NT2) belongs to WP-M and is deliberately absent: F1 draws six
 * rows, and none of them is notifications.
 *
 * WIRED. `GET /v1/account/profile` supplies the name, the number, the অপ্রাপ্য ক্যাশ block
 * and both row groups — INCLUDING the two drawn row values, `২টি` and `বাংলা`. Those are
 * facts F3 and F5 own, and the server computes them from the same read, so this screen
 * cannot claim two addresses while the address screen shows three. Composing `২টি` here
 * would also be composing Bangla, which docs/05 § conventions forbids.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, TabBar } from '@oprappo/ui';
import { formatTaka } from '@oprappo/contracts';
import type { AccountGlyph, AccountOverview } from '@oprappo/contracts';
import {
  Frame,
  MANROPE_THEN_ANEK_CLASS,
  RowChevron,
  SAND_FRAME,
} from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchAccount } from './api.js';

/** The six row glyphs, 19px, `2px`, teal. Three are § 01's; three are drawn only here. */
function RowGlyph({ glyph }: { glyph: AccountGlyph }): JSX.Element {
  if (glyph === 'account') return <Icon name="account" size={19} />;
  if (glyph === 'lock') return <Icon name="lock" size={19} />;
  return (
    <svg width={19} height={19} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      {glyph === 'pin' ? (
        <path
          d="M12 21s-7-4.5-7-10a7 7 0 0114 0c0 5.5-7 10-7 10z"
          stroke="#0E3A45"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      ) : glyph === 'card' ? (
        <>
          <rect x="3" y="5" width="18" height="14" rx="1.5" stroke="#0E3A45" strokeWidth={2} />
          <path d="M3 10h18" stroke="#0E3A45" strokeWidth={2} />
        </>
      ) : glyph === 'bookmark' ? (
        <path d="M6 4h12v16l-6-4-6 4z" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
      ) : (
        <>
          <circle cx="12" cy="12" r="9" stroke="#0E3A45" strokeWidth={2} />
          <path
            d="M3 12h18M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18"
            stroke="#0E3A45"
            strokeWidth={2}
          />
        </>
      )}
    </svg>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function F1AccountView({ data }: { data: AccountOverview | null }): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <div className="bg-teal px-[16px] py-[18px]">
        <button
          type="button"
          onClick={() => navigate('/account/profile')}
          className="w-full flex gap-[13px] items-center bg-transparent border-0 p-0 rounded-none text-left"
        >
          <span className="w-[52px] h-[52px] box-content rounded-full bg-teal-dark border border-white/[.22] flex items-center justify-center flex-none font-anek font-semibold text-[20px] leading-[1.75] text-amber">
            {data?.profile.initial_bn ?? ''}
          </span>
          <span className="flex-1">
            <span className="block font-anek font-semibold text-[18px] leading-[1.75] text-white">
              {data?.profile.name_bn ?? ''}
            </span>
            {/* Bangla numerals — Anek Bangla, not the drawn Manrope. See the file header. */}
            <span className="block font-anek font-normal text-[14px] leading-[1.4] text-on-teal-muted mt-[2px]">
              {data?.profile.phone_bn ?? ''}
            </span>
          </span>
          <svg width={17} height={17} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
            <path
              d="M9 6l6 6-6 6"
              stroke="#8FB0AE"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="flex-1 oprappo-scroll">
        <div className="bg-white border-b border-border px-[16px] py-[13px] flex gap-[12px] items-center">
          <div className="flex-1">
            <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted">
              {data?.cash.label_bn ?? ''}
            </div>
            <div className="font-manrope font-bold text-[20px] leading-[1.15] text-ink tabular-nums mt-[3px]">
              {data === null ? '' : formatTaka(data.cash.amount_minor)}
            </div>
          </div>
          {/*
           * Drawn `border:1.5px solid #0E3A45;padding:10px 14px;font:600 14px/1.75`.
           * § 02's Button has no padded 14px secondary — `sm` is a 44px-high box with a
           * 1px #E2DED5 rule, `inline` is a 14px type on a teal fill. Transcribed;
           * reported.
           */}
          <button
            type="button"
            className="border-[1.5px] border-teal px-[14px] py-[10px] font-anek font-semibold text-[14px] leading-[1.75] text-teal flex-none rounded-none bg-transparent"
          >
            {data?.cash.add_bn ?? ''}
          </button>
        </div>

        {(data?.groups ?? []).map((g, gi) => (
          <div key={g.heading_bn}>
            <div className={'px-[16px] pb-[11px] ' + (gi === 0 ? 'pt-[14px]' : 'pt-[15px]')}>
              <div
                className={`font-semibold text-[13px] leading-[1.75] tracking-[.09em] uppercase text-amber-text ${MANROPE_THEN_ANEK_CLASS}`}
              >
                {g.heading_bn}
              </div>
            </div>
            <div className="px-[16px]">
              {g.rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => navigate(r.to)}
                  className="w-full py-[13px] border-t border-border flex gap-[12px] items-center bg-transparent px-0 rounded-none text-left"
                >
                  <RowGlyph glyph={r.glyph} />
                  <span className="flex-1 font-anek font-normal text-[15.5px] leading-[1.75] text-ink2">
                    {r.label_bn}
                  </span>
                  {r.value_bn === null ? null : (
                    <span className="font-anek font-normal text-[14px] leading-[1.75] text-muted flex-none">
                      {r.value_bn}
                    </span>
                  )}
                  <RowChevron size={15} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <TabBar
        activeKey="account"
        onChange={(key) => {
          if (key === 'home') navigate('/');
          else if (key === 'activity') navigate('/activity');
          else if (key === 'explore') navigate('/explore');
          else if (key === 'ai') navigate('/ai');
        }}
      />
    </Frame>
  );
}

export function F1Account(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchAccount(signal), []);
  const { data, error } = useResource(load, 'account');

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <F1AccountView data={data} />
    </SurfaceStates>
  );
}
